const { jQuery: $ } = window;

import Hooks from "../modules/Hooks";
import StateStore from "../modules/StateStore";
import RestClient from "../modules/RestClient";
import Renderer from "../modules/Renderer";
import Pagination from "../modules/Pagination";
import AppliedFilters from "../modules/AppliedFilters";
import HistoryManager from "../modules/HistoryManager";
import ShortcodeInstance from "../modules/ShortcodeInstance";
import FilterRegistry from "../modules/FilterRegistry";
import SelectFilter from "../modules/filters/SelectFilter";
import CheckboxFilter from "../modules/filters/CheckboxFilter";
import RadioFilter from "../modules/filters/RadioFilter";
import ButtonGroupFilter from "../modules/filters/ButtonGroupFilter";

/**
 * @callback KPFfilter
 * @param {*} val
 * @param {*} ...args
 * @return {*}
 */

/**
 * @callback KPFaction
 * @param {*} val
 * @param {*} ...args
 */

/**
 * Page-level orchestrator: discovers the shortcodes on the page, builds the shared services (hook bus, REST
 * client, renderer, pagination, history), and creates one {@link ShortcodeInstance} per shortcode. Its public
 * surface (addFilter/addAction/data/settings/$el/logger/clearHistory/printHooks, the `kpf:ready` window event
 * and the `KMDGPostFilters.class` handle) is preserved exactly for theme back-compat.
 */
export default class PostFilters {
    /** @member {Logger} */
    #logger;

    /** @member {Mustache} */
    #mustache;

    /** @member {jQuery} All shortcode elements on the page. */
    #$el = null;

    /** @member {boolean} */
    #active = false;

    /** @member {boolean} */
    #loaded = false;

    /** @member {object} Page-level settings ({restBase, ajax}). */
    #settings = null;

    /** @member {Hooks} */
    #hooks;

    /** @member {FilterRegistry} */
    #filters;

    /** @member {Map<string, ShortcodeInstance>} */
    #instances = new Map();

    /** @member {object|null} Shared services passed to each instance. */
    #services = null;

    /**
     * @param {Logger} logger
     * @param {Mustache} mustache
     */
    constructor(logger, mustache) {
        this.#logger = logger;
        this.#mustache = mustache;
        this.#hooks = new Hooks(logger).setContext(this);

        // Built here (not in load()) so themes can registerFilterType() during kpf:ready. Ships the
        // built-in control types; themes can add/replace via registerFilterType() or the `services` filter.
        this.#filters = new FilterRegistry(logger)
            .register("select", SelectFilter)
            .register("checkbox", CheckboxFilter)
            .register("radio", RadioFilter)
            .register("button-group", ButtonGroupFilter);

        this.logger.start("Plugin Setup");

        this.#$el = $(".kmdg-post-filters");

        if(this.$el.length > 0) {
            this.#active = true;
            this.logger.log("Shortcode found on page.");
            this.logger.log("KPF Settings:");

            if(typeof KMDGPostFilters !== 'undefined') {
                KMDGPostFilters.class = this;
                this.#settings = KMDGPostFilters.settings;
                this.logger.table(this.#settings, ["Value"]);
                this.$el.find(".kpfsc__explore").remove();
                this.$el.removeClass("kpfsc--no-js");
            } else {
                this.logger.error("Settings object not found! Aborting...");
                this.#active = false;
            }
        } else {
            this.logger.log("Shortcode not found on page.");
        }

        this.logger.log("Status:", (this.#active ? "Active" : "Inactive"));

        this.logger.end();
    }

    /**
     * Fires the "kpf:ready" window event (so themes can register hooks) then boots the plugin. The double
     * setTimeout ensures theme code hooked on window-ready registers before load.
     */
    ready() {
        if(!this.#active) return;

        // Delay a tick so that other things registered on window ready can hook in.
        setTimeout(()=> {
            this.logger.log("Broadcast Ready Event (\"kpf:ready\")");
            $(window).trigger("kpf:ready", [this]);

            // After kpf:ready runs, load plugin
            setTimeout(()=> {
                this.load();
            }, 0);
        }, 0);
    }

    /**
     * Boots up the post filter system: builds shared services and one instance per shortcode.
     */
    load() {
        if(!this.#active) return;

        if(this.#loaded) {
            this.logger.error("Plugin already loaded!");
            return;
        }

        this.#loaded = true;
        this.logger.log("Loading...");

        // Default select2 options (page level)
        const select2Opts = this.#hooks.applyFilter("select2_options", {
            debug: this.logger.isDebug,
            dropdownAutoWidth: true,
            dropdownCssClass: "kpfsc--" + this.$el.attr("data-template") + "-template",
            selectionCssClass: "kpfsc--" + this.$el.attr("data-template") + "-template",
            minimumResultsForSearch: Infinity,
            allowClear: true,
        });

        this.#settings.ajax = this.#hooks.applyFilter("ajax_options", {
            dataType: "json",
            method: "GET",
        });

        // Shared services handed to every instance.
        this.#services = this.#hooks.applyFilter("services", {
            hooks: this.#hooks,
            logger: this.#logger,
            rest: new RestClient(this.#hooks, this.#logger, this.#settings),
            renderer: new Renderer(this.#hooks, this.#logger, this.#mustache),
            pagination: new Pagination(this.#hooks, this.#logger),
            appliedFilters: new AppliedFilters(this.#hooks, this.#logger),
            history: new HistoryManager(this.#logger, () => Array.from(this.#instances.values())),
            filters: this.#filters,
            select2Opts,
        });

        const savedStateMap = this.#services.history.getSavedState();

        // Build and boot one instance per shortcode element.
        this.$el.each((i, el) => {
            const $el = $(el);
            const store = new StateStore($el);
            const instance = new ShortcodeInstance($el, store, this.#services);

            this.#instances.set(el.id, instance);

            instance.init(savedStateMap ? savedStateMap[el.id] : null);
        });

        // Page-level back/forward handling.
        this.#services.history.listen();

        // On a shared/bookmarked filter URL, scroll to the shortcode it targets (the `@<form-id>` instance,
        // else the first history instance). No-op without a filter fragment. Deferred a tick so instances
        // that applied the fragment have laid out.
        setTimeout(() => {
            this.#services.history.scrollToTarget(this.#hooks.applyFilter("scroll_offset", 0));
        }, 0);

        this.#hooks.doAction("plugin_loaded");
    }

    /**
     * Registers a filter callback. Works like WordPress add_filter(); callbacks receive `this` bound to
     * this PostFilters instance.
     *
     * @param {string} name
     * @param {KPFfilter} callback
     * @param {int} priority
     */
    addFilter(name, callback, priority = 10) {
        this.#hooks.addFilter(name, callback, priority);
    }

    /**
     * Registers an action callback. Works like WordPress add_action().
     *
     * @param {string} name
     * @param {KPFaction} callback
     * @param {int} priority
     */
    addAction(name, callback, priority = 10) {
        this.#hooks.addAction(name, callback, priority);
    }

    /**
     * Registers a filter-control adapter type, making it available to templates that emit the matching
     * `data-filter-type`. `factory` is a FilterControl subclass, constructed as `new factory($filter,
     * instance)`. Call during the `kpf:ready` event.
     *
     * @param {string} name
     * @param {Function} factory A FilterControl subclass.
     */
    registerFilterType(name, factory) {
        this.#filters.register(name, factory);
    }

    /**
     * Backwards-compatible getter/setter for a shortcode's settings data. Reads when `value` is null
     * (whole object when `property` is also null), otherwise writes.
     *
     * @param {jQuery} $shortcode
     * @param {string|null} property
     * @param {*} value
     * @returns {*}
     */
    data($shortcode, property = null, value = null) {
        const instance = this.#instances.get($shortcode.attr("id"));

        if(instance) {
            const store = instance.store;
            if(value === null) {
                return property === null ? store.get() : store.get(property);
            }
            if(property === null) {
                store.replace(value);
            } else {
                store.set(property, value);
            }
            return;
        }

        // Fallback for elements not managed by an instance. Use StateStore.read so a script-only (no
        // data-settings attribute) form still resolves + seeds its jQuery cache.
        const settings = StateStore.read($shortcode);
        if(value === null) {
            return property === null ? settings : settings[property];
        }
        if(property === null) {
            $shortcode.data("settings", value);
        } else {
            settings[property] = value;
            $shortcode.data("settings", settings);
        }
    }

    /**
     * Clears the plugin's saved browser-history state.
     */
    clearHistory() {
        if(this.#services) {
            this.#services.history.clear();
        } else {
            try {
                history.replaceState({"kpf-shortcodes": {}}, "");
            } catch (e) {
                this.logger.warn("Failed to save history.");
            }
        }
    }

    /**
     * @returns {jQuery} The shortcode elements on the page.
     */
    get $el() {
        return this.#$el;
    }

    /**
     * @returns {Object} A clone of the page-level settings.
     */
    get settings() {
        return structuredClone(this.#settings);
    }

    /**
     * @returns {Logger}
     */
    get logger() {
        return this.#logger;
    }

    /**
     * Prints all registered actions and filters to the console.
     */
    printHooks() {
        this.logger
            .start("Actions & Filters")
            .groupStart("Actions", "", this.logger.COLOR.PURPLE)
            .dir(this.#hooks.hooks.actions)
            .groupEnd()
            .groupStart("Filters", "", this.logger.COLOR.PURPLE)
            .dir(this.#hooks.hooks.filters)
            .groupEnd()
            .end();
    }
}
